import { useReducer } from 'react';
import { Subject } from 'rxjs';
import { OmnichannelChatSDK } from '@microsoft/omnichannel-chat-sdk';
import { isProdEnv, type ILogger, type LiveAgentQueue } from '@bingads-webui-react/msa-copilot-common';
import ChatSDKConfig from '@microsoft/omnichannel-chat-sdk/lib/core/ChatSDKConfig';
import OmnichannelConfig from '@microsoft/omnichannel-chat-sdk/lib/core/OmnichannelConfig';

import { LiveAgentConnectionState } from '../helpers/live-agent-helper';
import { getOmnichannelConfig } from '../helpers/live-agent-config';

export type GetAuthTokenForQueueFunction = (queue?: LiveAgentQueue) => () => Promise<string | null>;

type LiveAgentChatSDKState = {
  chatSDK?: OmnichannelChatSDK,
  liveAgentState: LiveAgentConnectionState,
  liveAgentStream?: Subject<any>,
  omnichannelConfig?: OmnichannelConfig,
};

type LiveAgentChatSDKAction =
  | { type: 'initialize', createLiveAgentAuthTokenGetter: GetAuthTokenForQueueFunction, queue?: LiveAgentQueue }
  | { type: 'disconnect', logger?: ILogger, isLiveAgentOnlyMode?: boolean }
  | { type: 'in-progress' }
  | { type: 'connected' };

function createChatSDK(omnichannelConfig: OmnichannelConfig, createLiveAgentAuthTokenGetter?: GetAuthTokenForQueueFunction, queue?: LiveAgentQueue) {
  const chatSDKConfig: ChatSDKConfig = {
    chatReconnect: {
      disable: false,
    },
  };

  if (createLiveAgentAuthTokenGetter) {
    chatSDKConfig.getAuthToken = createLiveAgentAuthTokenGetter(queue);
  }

  return new OmnichannelChatSDK(omnichannelConfig, chatSDKConfig);
}

function liveAgentChatSDKReducer(state: LiveAgentChatSDKState, action: LiveAgentChatSDKAction): LiveAgentChatSDKState {
  switch (action.type) {
    case 'initialize': {
      if (state.chatSDK) {
        // already initialized, nothing to do
        return state;
      }

      const liveAgentStream = new Subject();
      const chatSDK = createChatSDK(state.omnichannelConfig!, action.createLiveAgentAuthTokenGetter, action.queue);

      return {
        ...state,
        chatSDK,
        liveAgentState: LiveAgentConnectionState.DISCONNECTED,
        liveAgentStream,
      };
    }
    case 'disconnect': {
      // cleanup old state
      state.liveAgentStream?.complete();
      state.chatSDK?.endChat().catch((ex) => {
        action.logger?.error(`Copilot - Live Agent: Error while ending chat: ${ex}`);
      });

      return {
        ...state,
        chatSDK: undefined,
        liveAgentState: LiveAgentConnectionState.DISCONNECTED,
        liveAgentStream: action?.isLiveAgentOnlyMode ? state.liveAgentStream : undefined,
      };
    }
    case 'in-progress':
      return {
        ...state,
        liveAgentState: LiveAgentConnectionState.IN_PROGRESS,
      };
    case 'connected':
      return {
        ...state,
        liveAgentState: LiveAgentConnectionState.CONNECTED,
      };
    default:
      return state;
  }
}

export function useLiveAgentChatSDKReducer(
  locale: string,
  createLiveAgentAuthTokenGetter?: (queue?: LiveAgentQueue) => () => Promise<string | null>,
  isLiveAgentOnlyMode?: boolean,
  logger?: ILogger,
) {
  return useReducer(
    liveAgentChatSDKReducer,
    { liveAgentState: LiveAgentConnectionState.DISCONNECTED },
    (initialState) => {
      const isProd = isProdEnv();
      const omnichannelConfig = getOmnichannelConfig(locale, isProd, !!createLiveAgentAuthTokenGetter);

      if (isLiveAgentOnlyMode) {
        logger?.trace('Copilot - Live Agent: isLiveAgentOnlyMode is true, immediately start the chat');
        const liveAgentStream = new Subject();
        const chatSDK = createChatSDK(omnichannelConfig, createLiveAgentAuthTokenGetter);
        return {
          ...initialState,
          chatSDK,
          liveAgentStream,
        };
      }

      return {
        ...initialState,
        omnichannelConfig,
      };
    },
  );
}
